// ============================================
// Demo Walkthrough — Male Patient (Low T)
// SEEN -> RIGHT -> SAFE -> ACT
//
// SEEN:  He feels heard before giving data
// RIGHT: His doctor was wrong. Here's proof.
// SAFE:  Supplements first. Low cost. Retest.
// ACT:   One tap. Start the protocol.
//
// Auto-plays a synthetic male patient journey
// for Stan to demo to Tom and Kevin.
// Uses maleDemoDataSource. Works on mobile.
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  ChatDataSource,
  BarMarker,
  BarSymptom,
  BarProtocol,
} from '../../types/analystBar';
import type { CapturedSignal } from '../../types/fashionPlate';
import type { AnalystPhase2Output, ModifierSet } from '../../types/patientJourney';
import { EMPTY_MODIFIER_SET } from '../../types/patientJourney';
import type { GeminiMessage } from '../../services/ai/providers/gemini';
import { AnalystBar } from './analyst-bar/AnalystBar';
import { SEED_SUPPLEMENTS } from '../../data/formulary/seedSupplements';
import { parseMonthlyPrice } from '../../services/patientJourney/twinEngine';
import { MALE_DEMO_BIOMARKERS, MALE_DEMO_PROTOCOL, maleDemoDataSource } from '../../services/chat/maleDemoDataSource';
import { mapBiomarkerToBarMarker } from '../../services/agents/labAnalystStream';

// -- Design Tokens --
const ROSE = '#E11D48';
const ROSE_LIGHT = 'rgba(225,29,72,0.08)';
const BG = '#FAFAFA';
const CARD = '#FFFFFF';
const BORDER = '#E4E4E7';
const BORDER_LIGHT = '#F4F4F5';
const TEXT = '#18181B';
const TEXT_MUTED = '#71717A';
const TEXT_FAINT = '#A1A1AA';
const GREEN = '#16A34A';
const GREEN_LIGHT = 'rgba(22,163,74,0.08)';
const R = 4;
const R_LG = 12;
const SHADOW_SM = '0 1px 3px rgba(0,0,0,0.04)';
const W = '#FFFFFF';

// -- Demo biomarkers pre-mapped to BarMarker format --
const DEMO_MARKERS: BarMarker[] = MALE_DEMO_BIOMARKERS.map(mapBiomarkerToBarMarker);

// -- Map CapturedSignal[] to BarSymptom[] --
function signalsToBarSymptoms(signals: CapturedSignal[]): BarSymptom[] {
  return signals
    .filter((s) => s.category === 'symptom')
    .map((s) => ({
      term: s.term.toUpperCase(),
      sourceText: s.sourceText,
      linkedBiomarker: s.linkedBiomarker,
    }));
}

// -- Build nurse system prompt with male patient context --
function buildNurseSystemPrompt(
  biomarkers: typeof MALE_DEMO_BIOMARKERS,
  protocol: AnalystPhase2Output,
  signals: CapturedSignal[],
  brandName: string,
  mdName: string,
  reinforcement: string,
): string {
  const bioSummary = biomarkers
    .filter((b) => b.hemologixStatus !== 'optimal')
    .map((b) => `${b.biomarker}: ${b.value} ${b.unit} (functional: ${b.functionalRange.low}-${b.functionalRange.high}, status: ${b.hemologixStatus})`)
    .join('\n');

  const protocolSummary = protocol.protocol
    .map((p) => `- ${p.productName}: ${p.dosage} (for ${p.triggerBiomarker})`)
    .join('\n');

  const signalSummary = signals.length > 0
    ? signals.map((s) => `- ${s.term} (${s.category})`).join('\n')
    : 'None yet';

  return `You are a ${brandName} health navigator working under ${mdName}'s clinical methodology.

ROLE: You are the Flight Attendant — warm, knowledgeable, never diagnostic. You identify gaps vs optimal ranges, NOT diagnoses. You never say "you have" or "you suffer from." You say "your levels suggest" or "this pattern is consistent with."

PATIENT CONTEXT: Male, 42, presented with fatigue, brain fog, and low energy. Labs analyzed. Protocol recommended.

BIOMARKERS (suboptimal/flagged):
${bioSummary}

RECOMMENDED PROTOCOL (Gate 1 — supplements only):
${protocolSummary}

CAPTURED SIGNALS FROM CONVERSATION:
${signalSummary}

RULES:
- Reference specific biomarker values when relevant (e.g., "your Testosterone at 340")
- Never fabricate data — only reference biomarkers listed above
- Never diagnose disease — identify gaps vs optimal ranges
- Never contradict the protocol — it was built by the formulary engine
- If patient mentions a NEW medication, allergy, or condition, acknowledge it and note it may affect the protocol
- Keep responses concise (2-4 sentences). Warm but efficient.
- No emojis. Professional but approachable tone.
${reinforcement ? `\nREINFORCEMENT:\n${reinforcement}` : ''}`;
}

// -- Bidirectional Highlighting --
function HighlightedText({ text, activeSymptoms }: { text: string; activeSymptoms: BarSymptom[] }) {
  if (!activeSymptoms || !activeSymptoms.length) return <>{text}</>;

  const matches: { start: number; end: number; word: string }[] = [];
  const textLower = text.toLowerCase();

  for (const symptom of activeSymptoms) {
    if (!symptom.sourceText) continue;
    const idx = textLower.indexOf(symptom.sourceText.toLowerCase());
    if (idx >= 0) {
      matches.push({ start: idx, end: idx + symptom.sourceText.length, word: text.slice(idx, idx + symptom.sourceText.length) });
    }
  }

  if (!matches.length) return <>{text}</>;
  matches.sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let c = 0;
  for (const m of matches) {
    if (m.start < c) continue;
    if (m.start > c) parts.push(<span key={c}>{text.slice(c, m.start)}</span>);
    parts.push(
      <span key={m.start} style={{
        background: 'rgba(255,255,255,0.28)', borderRadius: 3,
        padding: '1px 5px', margin: '0 -2px', fontWeight: 700,
        borderBottom: '2px solid #FFFFFF',
      }}>{m.word}</span>,
    );
    c = m.end;
  }
  if (c < text.length) parts.push(<span key={c}>{text.slice(c)}</span>);
  return <>{parts}</>;
}

// -- Icons --
const HeartPulseIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ROSE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /><path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" /></svg>;
const ShieldIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEXT_FAINT} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const FileCheckIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2" strokeLinecap="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><path d="m9 15 2 2 4-4" /></svg>;
const CheckIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>;
const SendIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>;

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 0' }}>
      {[0, 1, 2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: ROSE, animation: `td 1.4s ease-in-out ${i * 0.16}s infinite` }} />)}
    </div>
  );
}

// ==============================
// CONVERSION CARD -- THE ACT MOMENT
// ==============================
function ProtocolCard({ dataSource, dailyPrice }: { dataSource: ChatDataSource; dailyPrice: string }) {
  const [started, setStarted] = useState(false);
  const bd = dataSource.brandDisplay;

  const trustSignals = [
    `${bd.medicalDirectorName}'s methodology`,
    `Retest at ${bd.retestWeeks} weeks`,
    'Cancel anytime',
  ];

  const objectionKillers = [
    'No prescription needed',
    `Ships in ${bd.shippingDays} days`,
    ...(bd.hsaFsaEligible ? ['HSA/FSA eligible'] : []),
  ];

  return (
    <div style={{
      margin: '8px 0 4px', padding: '16px', background: CARD, border: `1px solid ${BORDER}`,
      borderRadius: R_LG, boxShadow: SHADOW_SM, animation: 'msgIn 0.3s ease',
    }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        {trustSignals.map(t => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Sans',sans-serif" }}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', background: GREEN, flexShrink: 0 }} />
            {t}
          </div>
        ))}
      </div>

      <button
        onClick={() => setStarted(true)}
        style={{
          width: '100%', padding: '14px 20px', fontSize: 15, fontWeight: 700,
          color: W, background: started ? GREEN : ROSE,
          border: 'none', borderRadius: R, cursor: 'pointer',
          fontFamily: "'DM Sans',sans-serif",
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'all 0.3s ease',
          boxShadow: started ? 'none' : '0 2px 12px rgba(225,29,72,0.3)',
        }}
      >
        {started ? <><CheckIcon /> Protocol Started</> : `Start My Protocol \u2014 $${dailyPrice}/day`}
      </button>

      {!started && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
          {objectionKillers.map(t => (
            <span key={t} style={{ fontSize: 10, color: TEXT_FAINT, fontFamily: "'DM Sans',sans-serif" }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ==============================
// MAIN — SEEN -> RIGHT -> SAFE -> ACT -> INTERACTIVE
// ==============================
interface ChatMessage {
  id: number;
  role: 'nurse' | 'user' | 'system';
  text: string;
}

export function DemoWalkthrough() {
  const dataSource = maleDemoDataSource;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [tickerMarkers, setTickerMarkers] = useState<BarMarker[]>([]);
  const [symptoms, setSymptoms] = useState<BarSymptom[]>([]);
  const [captainMsg, setCaptainMsg] = useState<string | null>(null);
  const [protocol, setProtocol] = useState<BarProtocol | null>(null);
  const [showCard, setShowCard] = useState(false);
  const [demoComplete, setDemoComplete] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasStarted = useRef(false);
  const signalsRef = useRef<CapturedSignal[]>([]);
  const turnRef = useRef(10);
  const chatHistoryRef = useRef<GeminiMessage[]>([]);
  const modifiersRef = useRef<ModifierSet>({ ...EMPTY_MODIFIER_SET });
  const nextIdRef = useRef(200);

  // Build protocol data
  const proto = dataSource.getProtocol([], { ...EMPTY_MODIFIER_SET });
  const barProducts = proto.protocol.map(p => {
    const seed = SEED_SUPPLEMENTS.find(s => s.id === p.productId);
    const monthlyPrice = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
    return { name: p.productName, monthlyPrice };
  });
  const totalMonthly = barProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);
  const dailyPrice = (totalMonthly / 30).toFixed(2);

  useEffect(() => {
    if (scrollRef.current) setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 80);
  }, [messages, isTyping, showCard, demoComplete]);

  const addMsg = useCallback((msg: ChatMessage) => setMessages(prev => [...prev, msg]), []);
  const wait = (ms: number) => new Promise(r => setTimeout(r, ms));
  const typing = useCallback(async (ms: number) => { setIsTyping(true); await wait(ms); setIsTyping(false); }, []);

  // -- Demo auto-play: Male patient — Low T, fatigue, brain fog --
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    (async () => {
      await wait(600);

      let signals: CapturedSignal[] = [];

      // ---- PHASE 1: SEEN ----
      await typing(1000);
      addMsg({ id: 1, role: 'nurse', text: "Hi \u2014 I'm your HemoLogix health navigator. What's been going on?" });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: "Hi \u2014 I'm your HemoLogix health navigator. What's been going on?" }] });
      await wait(2200);

      const patientMsg1 = "I'm wiped out all the time. Can't focus at work. My doctor ran a full panel and said everything looks fine, but I feel like I'm running on empty.";
      addMsg({ id: 3, role: 'user', text: patientMsg1 });
      chatHistoryRef.current.push({ role: 'user', parts: [{ text: patientMsg1 }] });
      await wait(700);

      signals = await dataSource.extractSymptoms(patientMsg1, 1, signals);
      const allSymptoms = signalsToBarSymptoms(signals);

      if (allSymptoms.length > 0) {
        setSymptoms([allSymptoms[0]]);
        await wait(1000);
        if (allSymptoms.length > 1) {
          setSymptoms(allSymptoms.slice(0, 2));
          await wait(600);
          if (allSymptoms.length > 2) {
            setSymptoms(allSymptoms.slice(0, 3));
          }
        }
      }
      await wait(1800);

      const nurseMsg2 = "You're not making this up. \"Normal\" lab ranges are built from a population that includes sick people \u2014 they're wide enough to miss most problems. Let me compare your panel to functional optimal ranges. Do you have those results handy?";
      await typing(1000);
      addMsg({ id: 4, role: 'nurse', text: nurseMsg2 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg2 }] });
      await wait(2200);

      const patientMsg2 = 'Yeah, I have my Quest results from two weeks ago.';
      addMsg({ id: 6, role: 'user', text: patientMsg2 });
      chatHistoryRef.current.push({ role: 'user', parts: [{ text: patientMsg2 }] });
      await wait(800);
      addMsg({ id: 100, role: 'system', text: 'Lab results received \u2014 Quest Diagnostics \u00B7 Feb 18, 2026' });
      await wait(500);

      // ---- PHASE 2: RIGHT ----
      await typing(600);
      addMsg({ id: 101, role: 'nurse', text: 'Running your panel now.' });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: 'Running your panel now.' }] });
      await wait(800);

      setCaptainMsg('Analyzing 12 biomarkers against functional optimal ranges');
      await wait(3000);
      setCaptainMsg(null);
      await wait(300);

      // Stream first 3 markers (attention-flagged) with dramatic pacing
      for (let i = 0; i < 3; i++) {
        setTickerMarkers(prev => [...prev, { ...DEMO_MARKERS[i], flag: 'pending' as BarMarker['flag'] }]);
        await wait(200);
        setTickerMarkers(prev => prev.map((m, mi) => mi === i ? { ...DEMO_MARKERS[i] } : m));
        await wait(400);
      }
      await wait(600);

      // Stream remaining markers faster
      for (let i = 3; i < DEMO_MARKERS.length; i++) {
        setTickerMarkers(prev => [...prev, { ...DEMO_MARKERS[i], flag: 'pending' as BarMarker['flag'] }]);
        await wait(120);
        setTickerMarkers(prev => prev.map((m, mi) => mi === i ? { ...DEMO_MARKERS[i] } : m));
        await wait(200);
      }
      await wait(400);

      // Link brain fog to biomarker
      setSymptoms(prev => {
        const hasBrainFog = prev.some(s => s.term === 'BRAIN FOG');
        if (!hasBrainFog) return [...prev, { term: 'BRAIN FOG', linkedBiomarker: 'Free Testosterone' }];
        return prev;
      });
      await wait(1200);

      setCaptainMsg('3 markers flagged \u00B7 your doctor called them normal');
      await wait(3500);
      setCaptainMsg(null);
      await wait(600);

      const nurseMsg3 = "Found it. Three markers your doctor called normal are actually significantly low by functional standards: Total Testosterone, Free Testosterone, and Vitamin D. Your Total T at 340 \u2014 your doctor sees 264 to 916 and says \"fine.\" But functional optimal starts at 500. You're running at 38% of where your body wants to be.";
      await typing(1200);
      addMsg({ id: 104, role: 'nurse', text: nurseMsg3 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg3 }] });
      await wait(2000);

      const nurseMsg4 = "And your Free Testosterone \u2014 the portion your body can actually use \u2014 is 8.2. Optimal is 15 to 25. With your SHBG elevated at 52, most of that already-low testosterone is locked up. That's the fatigue, the brain fog, the feeling of running on empty. You were right.";
      await typing(1000);
      addMsg({ id: 105, role: 'nurse', text: nurseMsg4 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg4 }] });
      await wait(2500);

      // ---- PHASE 3: SAFE ----
      setCaptainMsg('Gate 1 \u00B7 supplement protocol matched');
      await wait(2500);
      setCaptainMsg(null);
      await wait(400);

      setProtocol({
        gateLabel: 'GATE 1 \u00B7 SUPPLEMENT PROTOCOL',
        products: barProducts,
        dailyPrice: totalMonthly / 30,
      });
      await wait(600);

      const nurseMsg5 = `Good news \u2014 all three can be addressed with targeted supplements. No prescriptions needed for this first step. This is ${dataSource.brandDisplay.medicalDirectorName}'s protocol. We retest in ${dataSource.brandDisplay.retestWeeks} weeks. If supplements alone aren't enough, we have a doctor-supervised testosterone path \u2014 but let's see how your body responds first.`;
      await typing(1200);
      addMsg({ id: 106, role: 'nurse', text: nurseMsg5 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg5 }] });
      await wait(2000);

      // ---- PHASE 4: ACT ----
      const nurseMsg6 = `Four supplements. $${dailyPrice} a day. Most men notice the difference in 3 to 4 weeks \u2014 better energy, sharper focus, deeper sleep.`;
      await typing(800);
      addMsg({ id: 107, role: 'nurse', text: nurseMsg6 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg6 }] });
      await wait(400);

      setShowCard(true);

      // Store signals for interactive mode
      signalsRef.current = signals;

      // Demo complete — enable input
      await wait(1500);
      setDemoComplete(true);

    })();
  }, [addMsg, typing]);

  // -- Interactive message handler --
  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isSending) return;

    setInputText('');
    setIsSending(true);

    const msgId = nextIdRef.current++;
    const turn = ++turnRef.current;

    addMsg({ id: msgId, role: 'user', text });
    chatHistoryRef.current.push({ role: 'user', parts: [{ text }] });

    try {
      const newSignals = await dataSource.extractSymptoms(text, turn, signalsRef.current);
      if (newSignals.length > signalsRef.current.length) {
        signalsRef.current = newSignals;
        setSymptoms(signalsToBarSymptoms(newSignals));
      }
    } catch {
      // Extraction failure is non-blocking
    }

    const reveal = dataSource.detectLateReveal(text, turn);
    if (reveal && reveal.needsRerun) {
      modifiersRef.current = reveal.updatedModifiers;
      const newProto = dataSource.getProtocol(MALE_DEMO_BIOMARKERS, reveal.updatedModifiers);
      const newBarProducts = newProto.protocol.map(p => {
        const seed = SEED_SUPPLEMENTS.find(s => s.id === p.productId);
        const mp = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
        return { name: p.productName, monthlyPrice: mp };
      });
      const newTotal = newBarProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);

      setCaptainMsg('Protocol updated \u00B7 safety modifier detected');
      setProtocol({
        gateLabel: 'GATE 1 \u00B7 UPDATED PROTOCOL',
        products: newBarProducts,
        dailyPrice: newTotal / 30,
      });

      addMsg({
        id: nextIdRef.current++,
        role: 'system',
        text: `Safety update: ${reveal.safetyChanges.join(', ')} detected`,
      });

      setTimeout(() => setCaptainMsg(null), 3000);
    }

    const reinforcement = dataSource.getNurseReinforcement(signalsRef.current, turn);
    const systemPrompt = buildNurseSystemPrompt(
      MALE_DEMO_BIOMARKERS,
      MALE_DEMO_PROTOCOL,
      signalsRef.current,
      'HemoLogix',
      dataSource.brandDisplay.medicalDirectorName,
      reinforcement,
    );

    setIsTyping(true);
    try {
      const response = await dataSource.nurseRespond(
        chatHistoryRef.current,
        systemPrompt,
        text,
      );

      setIsTyping(false);
      if (response) {
        addMsg({ id: nextIdRef.current++, role: 'nurse', text: response });
        chatHistoryRef.current.push({ role: 'model', parts: [{ text: response }] });
      }
    } catch (err) {
      setIsTyping(false);
      console.error('[DemoWalkthrough] nurseRespond error:', err);
      addMsg({ id: nextIdRef.current++, role: 'system', text: 'Connection issue \u2014 try again' });
    }

    setIsSending(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [inputText, isSending, addMsg, dataSource]);

  useEffect(() => {
    if (demoComplete) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [demoComplete]);

  return (
    <div style={{ width: '100%', maxWidth: 480, height: '100dvh', margin: '0 auto', display: 'flex', flexDirection: 'column', background: BG, fontFamily: "'DM Sans',sans-serif", position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0} body{margin:0;background:${BG}}
        ::-webkit-scrollbar{width:0;height:0}
        @keyframes td{0%,60%,100%{opacity:.2;transform:translateY(0)}30%{opacity:.8;transform:translateY(-3px)}}
        @keyframes msgIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: CARD, borderBottom: `1px solid ${BORDER}`, boxShadow: SHADOW_SM, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: ROSE_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><HeartPulseIcon /></div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>Hemo<span style={{ color: ROSE }}>Logix</span></div>
            <div style={{ fontSize: 11, color: TEXT_FAINT, display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN }} /> Health Navigator
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_FAINT, display: 'flex', alignItems: 'center', gap: 4 }}><ShieldIcon /> HIPAA</div>
      </div>

      <AnalystBar markers={tickerMarkers} symptoms={symptoms} captainMsg={captainMsg} protocol={protocol} journeyStage="first_visit" />

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ textAlign: 'center', padding: '4px 0 12px' }}>
          <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, color: TEXT_FAINT, background: BORDER_LIGHT, padding: '4px 12px', borderRadius: 20 }}>Free AI Lab Analysis</div>
        </div>

        {messages.map(m => {
          if (m.role === 'system') return (
            <div key={m.id} style={{ textAlign: 'center', padding: '8px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: GREEN, fontWeight: 600, background: GREEN_LIGHT, padding: '6px 14px', borderRadius: 20 }}><FileCheckIcon /> {m.text}</div>
            </div>
          );
          if (m.role === 'user') return (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-end', padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ maxWidth: '82%', padding: '10px 14px', background: ROSE, color: W, borderRadius: `${R_LG}px ${R_LG}px ${R}px ${R_LG}px`, fontSize: 14, lineHeight: 1.55 }}>
                <HighlightedText text={m.text} activeSymptoms={symptoms} />
              </div>
            </div>
          );
          if (m.role === 'nurse') return (
            <div key={m.id} style={{ padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ maxWidth: '88%', padding: '10px 14px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: `${R_LG}px ${R_LG}px ${R_LG}px ${R}px`, fontSize: 14, lineHeight: 1.6, color: TEXT, boxShadow: SHADOW_SM }}>{m.text}</div>
            </div>
          );
          return null;
        })}

        {showCard && <ProtocolCard dataSource={dataSource} dailyPrice={dailyPrice} />}

        {isTyping && (
          <div style={{ padding: '3px 0', animation: 'msgIn 0.2s ease' }}>
            <div style={{ display: 'inline-flex', padding: '10px 14px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: `${R_LG}px ${R_LG}px ${R_LG}px ${R}px`, boxShadow: SHADOW_SM }}><TypingDots /></div>
          </div>
        )}
        <div style={{ height: 8 }} />
      </div>

      {/* Footer — input field after demo, disclaimer during */}
      {demoComplete ? (
        <div style={{
          padding: '10px 12px 12px', background: CARD, borderTop: `1px solid ${BORDER}`,
          animation: 'fadeIn 0.4s ease',
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about your results, protocol, side effects..."
              disabled={isSending}
              style={{
                flex: 1, padding: '10px 14px', fontSize: 14,
                border: `1px solid ${BORDER}`, borderRadius: R_LG,
                outline: 'none', fontFamily: "'DM Sans',sans-serif",
                color: TEXT, background: BG,
              }}
            />
            <button
              onClick={handleSend}
              disabled={isSending || !inputText.trim()}
              style={{
                width: 40, height: 40, borderRadius: '50%',
                background: inputText.trim() ? ROSE : BORDER,
                border: 'none', cursor: inputText.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s ease',
                flexShrink: 0,
              }}
            >
              <SendIcon />
            </button>
          </div>
          <div style={{ fontSize: 10, color: TEXT_FAINT, textAlign: 'center', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <ShieldIcon /> AI health navigator &middot; Not a substitute for medical advice
          </div>
        </div>
      ) : (
        <div style={{ padding: '10px 12px 12px', background: CARD, borderTop: `1px solid ${BORDER}`, textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: TEXT_FAINT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><ShieldIcon /> AI health navigator &middot; Not a substitute for medical advice</div>
        </div>
      )}
    </div>
  );
}
